const BLOCK_PRINT_FOCUS = false;
const DISABLE_AUTO_PRINT = false;

interface GeneratePdfOptions {
  autoPrint?: boolean;
  closeAfterPrint?: boolean;
  debug?: boolean;
  toolbarTitle?: string;
  pageMargin?: string;
}

const html_template = `<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{title}}</title>
    <style>
        @page {
            size: A4 {{orientation}};
            box-sizing: border-box;
            margin: 0;
        }

        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          @page {
            margin: {{page_margin}};
          }
          
          html, body {
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
            overflow: visible;
          }
          
          footer {
            page-break-after: always;
          }
        }

        html, body {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
        }

        #print-preview-root {
          width: 100%;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          box-sizing: border-box;
        }

        #print-preview-page {
          width: {{page_width}}px;
          height: {{page_height}}px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
          box-sizing: border-box;
        }

        #print-debug-toolbar {
          width: 100%;
          padding: 12px 16px;
          box-sizing: border-box;
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        #print-debug-toolbar button {
          background: #499f99;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-weight: 600;
          cursor: pointer;
        }

        #print-debug-toolbar .print-debug-title {
          font-weight: 600;
          color: #111827;
        }

        @media print {
          #print-preview-root {
            background: transparent;
            display: block;
            height: auto;
          }

          #print-preview-page {
            width: 100%;
            height: auto;
            box-shadow: none;
            background: transparent;
            margin: 0;
            padding: 0;
            overflow: visible;
          }

          #print-debug-toolbar {
            display: none;
          }
        }
    </style>
</head>
<body class="h-full flex flex-col">
    {{debug_toolbar}}
    <div id="print-preview-root">
      <div id="print-preview-page">
        {{content}}
      </div>
    </div>
    <script>
      (function () {
        const blockPrintFocus = {{block_print_focus}};
        const disableAutoPrint = {{disable_auto_print}};
        const debugMode = {{debug_mode}};
        const ensureTailwind = () =>
          new Promise((resolve) => {
            const existing = document.querySelector('link[href="/tailwind.css"]');
            if (existing instanceof HTMLLinkElement) {
              if (existing.sheet) return resolve(null);
              existing.addEventListener("load", () => resolve(null), { once: true });
              existing.addEventListener("error", () => resolve(null), { once: true });
              return;
            }
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.type = "text/css";
            link.href = "/tailwind.css";
            link.addEventListener("load", () => resolve(null), { once: true });
            link.addEventListener("error", () => resolve(null), { once: true });
            document.head.appendChild(link);
          });

        const waitForAssets = () => {
          const assets = Array.from(
            document.querySelectorAll("img, object, iframe, embed")
          );
          if (assets.length === 0) return Promise.resolve();

          return Promise.all(
            assets.map(
              (asset) =>
                new Promise((resolve) => {
                  const finish = () => resolve(null);
                  if (asset instanceof HTMLImageElement && asset.complete) {
                    return resolve(null);
                  }
                  asset.addEventListener("load", finish, { once: true });
                  asset.addEventListener("error", finish, { once: true });
                })
            )
          );
        };

        const triggerPrint = async () => {
          await ensureTailwind();
          await waitForAssets();
          setTimeout(() => {
            if (disableAutoPrint) return;
            if (!blockPrintFocus) {
              window.focus();
            }
            window.print();
          }, 100);
        };

        if (debugMode) {
          const debugButton = document.getElementById("print-debug-button");
          if (debugButton) {
            debugButton.addEventListener("click", () => {
              if (!blockPrintFocus) {
                window.focus();
              }
              window.print();
            });
          }
        }

        if (document.readyState === "complete") {
          triggerPrint();
        } else {
          window.addEventListener("load", triggerPrint, { once: true });
        }
      })();
    </script>
</body>
</html>`;

const generatePdf = (
  title: string,
  orientation: string,
  content: string,
  options: GeneratePdfOptions = {},
) => {
  const isLandscape = orientation === "landscape";
  const pageWidth = isLandscape ? 1122 : 794;
  const pageHeight = isLandscape ? 794 : 1122;
  const debugMode = options.debug ?? false;
  const autoPrint = options.autoPrint ?? (!DISABLE_AUTO_PRINT && !debugMode);
  const toolbarTitle = options.toolbarTitle ?? "Print Preview";
  const pageMargin = options.pageMargin ?? "8mm 8mm";
  const closeAfterPrint = options.closeAfterPrint ?? !debugMode;

  let template = html_template;

  template = template
    .replace("{{title}}", title)
    .replace("{{orientation}}", orientation)
    .replace("{{content}}", content)
    .replace("{{block_print_focus}}", BLOCK_PRINT_FOCUS ? "true" : "false")
    .replace("{{disable_auto_print}}", autoPrint ? "false" : "true")
    .replace("{{debug_mode}}", debugMode ? "true" : "false")
    .replace("{{page_margin}}", pageMargin)
    .replace("{{page_width}}", pageWidth.toString())
    .replace("{{page_height}}", pageHeight.toString())
    .replace(
      "{{debug_toolbar}}",
      debugMode
        ? `<div id="print-debug-toolbar">
             <span class="print-debug-title">{{toolbar_title}}</span>
             <button id="print-debug-button" type="button">Print</button>
           </div>`
        : "",
    )
    .replace("{{toolbar_title}}", toolbarTitle);
  const printWidow = window.open("", "_blank");
  if (!printWidow) return;
  printWidow.document.write(template);
  printWidow.document.close();
  if (!BLOCK_PRINT_FOCUS) {
    printWidow.focus();
  }
  if (!autoPrint) return;
  const attemptPrint = () => {
    if (printWidow.closed) return;
    try {
      if (!BLOCK_PRINT_FOCUS) {
        printWidow.focus();
      }
      printWidow.print();
    } catch (error) {
      // Ignore print errors to avoid blocking UX.
    }
  };
  // Retry printing in case the load-based trigger is blocked.
  setTimeout(attemptPrint, 300);
  setTimeout(attemptPrint, 1200);
  printWidow.addEventListener("load", attemptPrint, { once: true });
  if (closeAfterPrint) {
    printWidow.addEventListener("afterprint", () => {
      printWidow.close();
    });
  }
};
export default generatePdf;
